import React from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

export interface DownstreamStaleness {
  is_stale: boolean;
  changed_at: string | null;
  changes: string[];
  summary: string | null;
}

interface DownstreamStalenessBannerProps {
  staleness?: DownstreamStaleness | null;
  artifactLabel: string;
  onRegenerate?: () => void;
  isRegenerating?: boolean;
  className?: string;
}

/**
 * Step 2 is the approval gate for who and what is in scope, but an artifact
 * generated before an edit there is a stored snapshot — it keeps the deleted
 * stakeholder's swim lane and the old name until it is regenerated.
 *
 * Regenerating automatically would throw away manual map edits, so the choice
 * stays with the user. This names exactly what changed so that choice is an
 * informed one rather than a guess.
 */
const DownstreamStalenessBanner: React.FC<DownstreamStalenessBannerProps> = ({
  staleness,
  artifactLabel,
  onRegenerate,
  isRegenerating = false,
  className = '',
}) => {
  if (!staleness?.is_stale || !staleness.changes?.length) return null;

  const changedAt = staleness.changed_at ? new Date(staleness.changed_at) : null;
  const changedAtLabel =
    changedAt && !Number.isNaN(changedAt.getTime()) ? changedAt.toLocaleString() : null;

  return (
    <div
      role="status"
      className={`rounded-xl border-2 border-amber-300 bg-amber-50 p-5 shadow-sm ${className}`}
    >
      <div className="flex items-start gap-4">
        <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-amber-300 bg-amber-100">
          <AlertTriangle className="h-5 w-5 text-amber-700" />
        </div>

        <div className="min-w-0 flex-1">
          <h4 className="text-sm font-bold text-amber-900">
            This {artifactLabel} is out of date
          </h4>
          <p className="mt-1 text-sm text-amber-800">
            Stakeholders or systems were changed at Step 2 after this {artifactLabel} was
            generated, so it still shows the old values.
          </p>

          <ul className="mt-3 space-y-1">
            {staleness.changes.map((change, index) => (
              <li key={index} className="flex items-start gap-2 text-sm text-amber-900">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />
                <span>{change}</span>
              </li>
            ))}
          </ul>

          {changedAtLabel && (
            <p className="mt-3 text-xs font-medium text-amber-700">
              Approved at Step 2 on {changedAtLabel}
            </p>
          )}

          {onRegenerate && (
            <div className="mt-4">
              <button
                type="button"
                onClick={onRegenerate}
                disabled={isRegenerating}
                className="inline-flex items-center gap-2 rounded-lg border-2 border-amber-400 bg-white px-4 py-2 text-sm font-semibold text-amber-900 transition-colors hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <RefreshCw className={`h-4 w-4 ${isRegenerating ? 'animate-spin' : ''}`} />
                {isRegenerating ? 'Regenerating...' : `Regenerate ${artifactLabel}`}
              </button>
              <p className="mt-2 text-xs text-amber-700">
                Regenerating replaces this {artifactLabel}. Any manual edits made to it will be lost.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DownstreamStalenessBanner;
